import { useEffect, useState } from "react";
import { Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { isRichTextContentEmpty } from "../../../../../utils/rich-text-empty";
import { CloudUpload } from "@mui/icons-material";
import { useSnackbar } from "../../../../../components/snackbar";
import { Item } from "../preview-model";
import { RenderRichText } from "../../../components/rich-text/render-rich-text";

const MAX_FILE_SIZE_MB = 50;
const ALLOWED_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

interface FileUploadViewProps {
    item: Item;
    register: (id: string, getItem: () => Item) => void;
    setResource: React.Dispatch<React.SetStateAction<string>>;
}

export default function FileUploadView({ item, register, setResource }: FileUploadViewProps) {
    const [locItem, setLocItem] = useState<Item>({
        ...item,
        answer: item.answer ? item.answer : null
    });
    const { showMessage } = useSnackbar();

    const handleAnswerChange = (value: any) => {
        setLocItem((prev) => ({ ...prev, answer: value }));
    };

    const handleFileChange = (e: any) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_TYPES.includes(file.type)) {
            showMessage('Invalid file type. Only .doc, .docx, or .pdf allowed.', 1);
            handleAnswerChange(null);
            return;
        }

        const fileSizeMB = file.size / (1024 * 1024);

        if (fileSizeMB > MAX_FILE_SIZE_MB) {
            showMessage('File is too large. Maximum size is 50MB.', 1);
            handleAnswerChange(null);
            return;
        }
        handleAnswerChange(file);
    };

    useEffect(() => {
        register(item.id, () => locItem);
    }, [item.id, locItem, register]);


    return (
        <Paper
            key={item.id}
            elevation={1}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 3,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                backgroundColor: '#fff',
                gap: 2
            }}
        >
            {(item.title || item.required || item.marks) && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {!isRichTextContentEmpty(item.title) && (
                            <RenderRichText jsonContent={item.title} />
                        )}
                        {(item.type !== 0 && item.required) && (
                            <Typography color="error">*</Typography>
                        )}
                    </Box>

                        <Typography variant="caption" color="text.secondary">
                        {item.marks} mark{item.marks !== 1 ? 's' : ''}
                    </Typography>
                </Box>
            )}

            {!isRichTextContentEmpty(item.description) && (
                <Box>
                    <RenderRichText jsonContent={item.description} />
                </Box>
            )}
            
            {item.resources?.length > 0 && (
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ overflowX: "auto",}}
                >
                    {item.resources.map((res, i) => {
                        const handleResourceClick = () => {
                            setResource(res);
                        };

                        return (
                            <Box
                                key={i}
                                component="img"
                                src={res}
                                alt={`resource-${i}`}
                                onClick={handleResourceClick}
                                sx={{
                                    width: 48,
                                    height: 48,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    border: '1px solid #ccc',
                                }}
                            />
                        );
                    })}
                </Stack>
            )}
    
            <Box
                sx={{
                    border: '1px dashed #ccc',
                    borderRadius: 1,
                    padding: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f9f9f9',
                    marginBottom: 2,
                    textAlign: 'center',
                }}
            >
                <CloudUpload sx={{ fontSize: 20, color: '#999', mb: 1, display: 'flex' }} />

                <input
                    type="file"
                    id="file-upload"
                    accept=".doc,.docx,.pdf"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />

                <label htmlFor="file-upload">
                    <Button variant="outlined" component="span" sx={{ mt: 1 }}>
                        Browse Files
                    </Button>
                </label>

                <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                    Maximum file size: 50MB
                </Typography>

                {locItem.answer && (
                    <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
                        Selected file: {locItem.answer.name}
                    </Typography>
                )}
            </Box>


        </Paper>
    )
}
